import { datadogRum } from '@datadog/browser-rum';
import React from 'react';

import { reloadPage } from '../../utils/navigation';

interface State {
  hasError: boolean;
}

/**
 * Top-level error boundary — catches unhandled React render errors and shows a
 * recovery modal instead of a blank page. Must be a class component; hooks
 * cannot be used in error boundaries.
 */
class ErrorBoundary extends React.Component<React.PropsWithChildren, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.log('ErrorBoundary caught an unhandled error:', error, info.componentStack);
    datadogRum.addError(error, { componentStack: info.componentStack });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="spc">
          {/* Intentionally not using Modal/FocusTrap — those may themselves be broken. */}
          <div
            className="spc-modal-v2"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="crash-error-title"
            aria-describedby="crash-error-body"
          >
            <div className="spc-modal-content-v2">
              <div className="spc-modal-gutter-lg">
                <h2 className="spc-verification-h2 mod-font" id="crash-error-title">
                  Something went wrong
                </h2>
                <p className="spc-verification-text" id="crash-error-body">
                  An unexpected error occurred. Please reload the page to continue.
                </p>
                <div className="spc-verification-edit">
                  <button
                    onClick={reloadPage}
                    type="button"
                    className="spc-verification-div-button"
                  >
                    Reload page
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export { ErrorBoundary };
